import React from 'react';
import { Component, ComponentProps, deps, inject, observer } from '../../lib/component';
import { TrackSchema } from '../../types/schema';
import { PlayerController } from './controller';
import { PlayerModel } from './model';

export interface WithPlayerCompatibleComponentProps {
  track: TrackSchema;
}

export type WithPlayerComponentProps = WithPlayerCompatibleComponentProps & {
  playerController: PlayerController;
  playerModel: PlayerModel;
  isCurrent: boolean;
  isCurrentPlaying: boolean;
  onPause: PlayerController['pause'];
  onPlay: PlayerController['play'];
  onLoadImmediate: PlayerController['loadImmediateAndSkip'];
};

export default function withPlayer<T extends WithPlayerComponentProps = WithPlayerComponentProps>(
  WrappedComponent: React.ComponentType<T>,
) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithPlayer = inject(deps)(
    observer(
      (
        props: Omit<T, keyof WithPlayerComponentProps> & ComponentProps & WithPlayerCompatibleComponentProps,
      ) => {
        const playerController = props.controller.player;
        const playerModel = props.model.player;
        const { currentTrack, play } = playerModel;
        const { track } = props;

        const isCurrent = currentTrack && track.identity === currentTrack.identity;
        const isCurrentPlaying = play && isCurrent;

        return (
          <WrappedComponent
            playerController={playerController}
            playerModel={playerModel}
            isCurrent={isCurrent}
            isCurrentPlaying={isCurrentPlaying}
            onLoadImmediate={playerController.loadImmediateAndSkip}
            onPlay={playerController.play}
            onPause={playerController.pause}
            {...(props as T)}
          />
        );
      },
    ),
  );

  ComponentWithPlayer.displayName = `withPlayer(${displayName})`;

  return ComponentWithPlayer;
}
